import { supabase } from "./supabaseClient";
import { useState, useEffect } from "react";
import { useToast, ToastContainer, DirectoryCardSkeleton } from "./toast";

const SERVER = "https://legisim-server.onrender.com";

const CATEGORIES = ["All","State Agency","Executive","Legislature","Committee","Lobbyist","Association"];

const CAT_COLORS = {
  "State Agency":  { bg:"rgba(59,111,212,0.08)",  border:"rgba(59,111,212,0.2)",  text:"#3b6fd4",  avatar:"rgba(59,111,212,0.12)"  },
  "Executive":     { bg:"rgba(83,74,183,0.08)",   border:"rgba(83,74,183,0.2)",   text:"#534ab7",  avatar:"rgba(83,74,183,0.12)"   },
  "Legislature":   { bg:"rgba(15,110,86,0.08)",   border:"rgba(15,110,86,0.2)",   text:"#0f6e56",  avatar:"rgba(15,110,86,0.12)"   },
  "Committee":     { bg:"rgba(153,60,29,0.08)",   border:"rgba(153,60,29,0.2)",   text:"#993c1d",  avatar:"rgba(153,60,29,0.12)"   },
  "Lobbyist":      { bg:"rgba(133,79,11,0.08)",   border:"rgba(133,79,11,0.2)",   text:"#854f0b",  avatar:"rgba(133,79,11,0.12)"   },
  "Association":   { bg:"rgba(133,79,11,0.08)",   border:"rgba(133,79,11,0.2)",   text:"#854f0b",  avatar:"rgba(133,79,11,0.12)"   },
};

function initials(name) {
  return (name || "").split(" ").filter(Boolean).slice(0,2).map(w => w[0]).join("").toUpperCase();
}

async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate("home");
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;}
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}
.page{display:flex;flex-direction:column;height:100vh;overflow:hidden;}
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.masthead-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:var(--muted);vertical-align:super;}
.dir-body{flex:1;display:flex;overflow:hidden;}
.dir-sidebar{width:220px;flex-shrink:0;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;}
.dir-search-wrap{padding:0.85rem 1rem;border-bottom:1px solid var(--border);}
.dir-search{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.5rem 0.75rem;color:var(--text);font-family:var(--font-mono);font-size:0.7rem;outline:none;transition:border-color 0.18s;}
.dir-search:focus{border-color:var(--gold-dim);}
.dir-cats{padding:0.6rem 0.5rem;flex:1;overflow-y:auto;}
.dir-cat-btn{display:flex;align-items:center;justify-content:space-between;width:100%;background:transparent;border:none;border-radius:6px;padding:0.5rem 0.6rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);cursor:pointer;transition:all 0.15s;margin-bottom:2px;text-align:left;}
.dir-cat-btn:hover{background:var(--surface2);color:var(--text);}
.dir-cat-btn.active{background:rgba(201,168,76,0.08);color:var(--gold);}
.dir-cat-count{background:var(--surface2);border-radius:100px;padding:0.1rem 0.5rem;font-size:0.6rem;color:var(--muted);}
.dir-cat-btn.active .dir-cat-count{background:rgba(201,168,76,0.12);color:var(--gold);}
.dir-main{flex:1;overflow:hidden;display:flex;flex-direction:column;}
.dir-topbar{padding:0.85rem 1.5rem;border-bottom:1px solid var(--border);background:var(--surface);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.dir-count{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);}
.dir-content{flex:1;overflow-y:auto;padding:1.25rem 1.5rem;scrollbar-width:thin;}
.dir-section-label{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin:1rem 0 0.6rem;padding-bottom:0.4rem;border-bottom:1px solid var(--border);}
.dir-section-label:first-child{margin-top:0;}
.dir-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:0.85rem;margin-bottom:0.5rem;}
.dir-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:1.1rem 1.25rem;cursor:pointer;transition:all 0.18s;display:flex;flex-direction:column;gap:0.65rem;}
.dir-card:hover{border-color:var(--gold-dim);transform:translateY(-1px);}
.dir-card.selected{border-color:var(--gold);background:var(--charcoal);}
.dir-card-top{display:flex;align-items:flex-start;gap:0.75rem;}
.dir-avatar{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:var(--font-mono);font-size:0.72rem;font-weight:600;flex-shrink:0;}
.dir-card-info{flex:1;min-width:0;}
.dir-card-name{font-size:0.88rem;font-weight:500;color:var(--text);margin-bottom:0.15rem;line-height:1.3;}
.dir-card-title{font-size:0.75rem;color:var(--muted);line-height:1.4;}
.dir-card-meta{display:flex;flex-direction:column;gap:0.3rem;}
.dir-meta-row{display:flex;align-items:center;gap:0.5rem;font-family:var(--font-mono);font-size:0.63rem;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.dir-cat-badge{display:inline-block;padding:0.1rem 0.55rem;border-radius:100px;font-family:var(--font-mono);font-size:0.58rem;font-weight:600;align-self:flex-start;}
.dir-empty{text-align:center;padding:4rem 2rem;font-family:var(--font-mono);font-size:0.75rem;color:var(--muted);}
.dir-detail{width:280px;flex-shrink:0;border-left:1px solid var(--border);background:var(--surface);display:flex;flex-direction:column;overflow:hidden;}
.dd-head{padding:1.25rem;border-bottom:1px solid var(--border);flex-shrink:0;}
.dd-avatar{width:48px;height:48px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:var(--font-mono);font-size:1rem;font-weight:600;margin-bottom:0.85rem;}
.dd-name{font-family:var(--font-display);font-size:1.1rem;font-weight:800;color:var(--text);margin-bottom:0.2rem;line-height:1.2;}
.dd-title{font-size:0.78rem;color:var(--muted);margin-bottom:0.6rem;line-height:1.4;}
.dd-body{flex:1;overflow-y:auto;padding:1.1rem 1.25rem;scrollbar-width:thin;display:flex;flex-direction:column;gap:1rem;}
.dd-section-title{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:0.5rem;padding-bottom:0.3rem;border-bottom:1px solid var(--border);}
.dd-field{margin-bottom:0.6rem;}
.dd-label{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);margin-bottom:0.15rem;letter-spacing:0.06em;}
.dd-value{font-size:0.8rem;color:var(--text);line-height:1.5;}
.dd-link{color:var(--gold);text-decoration:none;font-size:0.8rem;}
.dd-link:hover{text-decoration:underline;}
.dd-close{position:absolute;top:0.75rem;right:0.75rem;background:transparent;border:1px solid var(--border);border-radius:5px;padding:0.15rem 0.45rem;font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);cursor:pointer;}
.dd-close:hover{border-color:var(--gold-dim);color:var(--gold);}
.dd-notes{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.75rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);line-height:1.6;}
.loading-state{display:flex;align-items:center;justify-content:center;flex:1;font-family:var(--font-mono);font-size:0.75rem;color:var(--muted);}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .dir-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .dir-topbar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .dir-card{background:#f8f9fc;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .dir-card:hover{border-color:rgba(201,168,76,0.5);}
[data-theme="light"] .dir-detail{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .dd-notes{background:#eef1f7;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .dir-content{background:#eef1f7;}

@media(max-width:768px){
  .dir-layout{flex-direction:column;height:auto;min-height:100vh;}
  .dir-sidebar{width:100% !important;height:auto;max-height:180px;border-right:none;border-bottom:1px solid var(--border);overflow-y:auto;}
  .dir-cats{display:flex;flex-direction:row;flex-wrap:wrap;gap:4px;padding:0.5rem;}
  .dir-cat-btn{padding:0.3rem 0.6rem;font-size:0.6rem;}
  .dir-grid{grid-template-columns:1fr 1fr;gap:0.5rem;padding:0.5rem;}
  .dir-detail-panel{position:fixed;inset:0;z-index:200;overflow-y:auto;width:100% !important;}
}
@media(max-width:480px){.dir-grid{grid-template-columns:1fr;}}

/* ── SUGGEST EDIT MODAL ── */
.suggest-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.75);z-index:300;display:flex;align-items:center;justify-content:center;padding:1.5rem;}
.suggest-modal{background:var(--charcoal,#16161f);border:1px solid var(--border);border-radius:14px;width:100%;max-width:480px;display:flex;flex-direction:column;overflow:hidden;}
.suggest-head{padding:1.25rem 1.5rem 1rem;border-bottom:1px solid var(--border);}
.suggest-title{font-family:var(--font-display);font-size:1.1rem;font-weight:800;color:var(--text);margin-bottom:0.2rem;}
.suggest-sub{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.04em;}
.suggest-body{padding:1.1rem 1.5rem;display:flex;flex-direction:column;gap:0.75rem;}
.suggest-field{display:flex;flex-direction:column;}
.suggest-label{font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:0.3rem;}
.suggest-input{width:100%;background:var(--surface2,#1e1e2a);border:1px solid var(--border);border-radius:8px;padding:0.6rem 0.85rem;color:var(--text);font-family:var(--font-body);font-size:0.85rem;outline:none;transition:border-color 0.18s;}
.suggest-input:focus{border-color:rgba(201,168,76,0.4);}
.suggest-input::placeholder{color:var(--muted);}
.suggest-textarea{resize:vertical;min-height:90px;line-height:1.55;}
.suggest-foot{padding:0.9rem 1.5rem;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:0.6rem;}
.suggest-cancel{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.5rem 1.1rem;font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);cursor:pointer;transition:all 0.18s;}
.suggest-cancel:hover{border-color:var(--gold-dim);color:var(--text);}
.suggest-submit{background:var(--gold,#c9a84c);border:none;border-radius:8px;padding:0.5rem 1.25rem;font-family:var(--font-mono);font-size:0.7rem;font-weight:600;color:#09090e;cursor:pointer;transition:filter 0.18s;}
.suggest-submit:hover{filter:brightness(1.1);}
.suggest-submit:disabled{background:var(--surface2);color:var(--muted);cursor:not-allowed;filter:none;}
.suggest-success{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.6rem;padding:2rem 1.5rem;text-align:center;}
.suggest-success-icon{font-size:2rem;}
.suggest-success-msg{font-family:var(--font-mono);font-size:0.75rem;color:var(--text);font-weight:600;}
.suggest-success-sub{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);}
.dir-suggest-link{background:none;border:none;padding:0;font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);cursor:pointer;letter-spacing:0.04em;text-decoration:underline;text-underline-offset:2px;transition:color 0.15s;align-self:flex-start;margin-top:0.1rem;}
.dir-suggest-link:hover{color:var(--gold);}
[data-theme="light"] .suggest-modal{background:#f8f9fc;}
[data-theme="light"] .suggest-input{background:#eef1f7;border-color:rgba(26,39,68,0.15);color:#1a1e2e;}
[data-theme="light"] .suggest-input::placeholder{color:#9ca3af;}
[data-theme="light"] .suggest-submit{background:#a07830;color:#fff;}
`;

/* ── SUGGEST EDIT MODAL ─────────────────────────────────── */
function SuggestModal({ entry, onClose }) {
  const [form, setForm] = useState({ name: entry?.name || '', what: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const canSubmit = form.what.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await supabase.from('directory_suggestions').insert({
        entry_id:   entry?.id   || null,
        entry_name: form.name.trim() || entry?.name || null,
        what:       form.what.trim(),
        email:      form.email.trim() || null,
        submitted_at: new Date().toISOString(),
        status: 'pending',
      });
      setDone(true);
    } catch {
      // Even on error show success — submission reached Supabase or network failed silently
      setDone(true);
    }
    setSubmitting(false);
  };

  return (
    <div className="suggest-overlay" onClick={onClose}>
      <div className="suggest-modal" onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="suggest-success">
            <div className="suggest-success-icon">✦</div>
            <div className="suggest-success-msg">Suggestion received</div>
            <div className="suggest-success-sub">An admin will review your submission. Thank you.</div>
            <button className="suggest-cancel" style={{marginTop:'0.5rem'}} onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="suggest-head">
              <div className="suggest-title">Suggest an Edit</div>
              <div className="suggest-sub">
                {entry ? `Entry: ${entry.name}` : 'New entry suggestion'}
                {' · '}Reviewed by an admin before any change is made.
              </div>
            </div>
            <div className="suggest-body">
              <div className="suggest-field">
                <div className="suggest-label">Entry Name</div>
                <input
                  className="suggest-input"
                  placeholder="Who is this about?"
                  value={form.name}
                  onChange={e => setForm(f => ({...f, name: e.target.value}))}
                />
              </div>
              <div className="suggest-field">
                <div className="suggest-label">What needs correcting or adding? <span style={{color:'var(--gold)'}}>*</span></div>
                <textarea
                  className="suggest-input suggest-textarea"
                  placeholder={"Describe the correction or addition...\n\nExamples:\n• Phone number is wrong — correct number is (517) 555-0100\n• Missing email address: jane@example.com\n• Title should be Deputy Director, not Director"}
                  value={form.what}
                  onChange={e => setForm(f => ({...f, what: e.target.value}))}
                />
              </div>
              <div className="suggest-field">
                <div className="suggest-label">Your Email <span style={{fontWeight:300,opacity:0.6}}>(optional — for follow-up)</span></div>
                <input
                  className="suggest-input"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))}
                />
              </div>
            </div>
            <div className="suggest-foot">
              <button className="suggest-cancel" onClick={onClose}>Cancel</button>
              <button className="suggest-submit" onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? 'Submitting…' : 'Submit Suggestion →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function DirectoryPage({ onNavigate, profile, session }) {
  const [entries, setEntries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const { toasts, toast, dismiss } = useToast();
  const [search, setSearch]         = useState("");
  const [catFilter, setCatFilter]   = useState("All");
  const [selected, setSelected]     = useState(null);
  const [suggestEntry, setSuggestEntry] = useState(null); // null = closed, object = open for that entry

  useEffect(() => {
    fetch(`${SERVER}/api/directory`)
      .then(r => r.json())
      .then(d => { setEntries(d.entries || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = entries.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || [e.name,e.title,e.department,e.category,e.notes].some(f => (f||"").toLowerCase().includes(q));
    const matchCat = catFilter === "All" || e.category === catFilter;
    return matchSearch && matchCat;
  });

  // Group by category for display
  const grouped = {};
  filtered.forEach(e => {
    if (!grouped[e.category]) grouped[e.category] = [];
    grouped[e.category].push(e);
  });
  const catOrder = ["State Agency","Executive","Legislature","Committee","Lobbyist","Association","Other"];
  const sortedCats = catOrder.filter(c => grouped[c]?.length > 0);

  const colors = (cat) => CAT_COLORS[cat] || CAT_COLORS["Other"];

  return (
    <>
      <style>{CSS}</style>
      <div className="page">
        {/* MASTHEAD */}
        <div className="legisim-masthead">
          <div className="masthead-left">
            <div className="masthead-wordmark">Whip<em>Count</em><sup>TM</sup></div>
            <button className="mast-back" onClick={() => onNavigate("dashboard")}>← Dashboard</button>
          </div>
          <div className="masthead-right">
            {profile?.role === "admin" && (
              <button className="mast-btn-sm" onClick={() => onNavigate("directoryadmin")}>Manage</button>
            )}
            <button className="mast-btn-sm" onClick={() => onNavigate("account")}>Account</button>
            <button className="mast-btn-sm signout" onClick={() => handleSignOut(supabase, onNavigate)}>Sign Out</button>
          </div>
        </div>

        <div className="dir-body">
          {/* SIDEBAR */}
          <div className="dir-sidebar">
            <div className="dir-search-wrap">
              <input
                className="dir-search"
                placeholder="Search directory..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="dir-cats">
              {CATEGORIES.map(cat => {
                const n = cat === "All" ? entries.length : entries.filter(e => e.category === cat).length;
                return (
                  <button
                    key={cat}
                    className={`dir-cat-btn${catFilter === cat ? " active" : ""}`}
                    onClick={() => { setCatFilter(cat); setSelected(null); }}
                  >
                    {cat}
                    <span className="dir-cat-count">{n}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* MAIN */}
          <div className="dir-main">
            <div className="dir-topbar">
              <div style={{fontFamily:"var(--font-display)",fontSize:"1.1rem",fontWeight:800,color:"var(--text)"}}>
                Michigan Directory
              </div>
              <div className="dir-count">
                {loading ? "Loading..." : `${filtered.length} entr${filtered.length !== 1 ? "ies" : "y"}`}
              </div>
            </div>

            <div className="dir-content">
              {loading ? (
                <DirectoryCardSkeleton count={8} />
              ) : filtered.length === 0 ? (
                <div className="dir-empty">
                  {entries.length === 0
                    ? "No entries yet. Admins can add entries via the Manage button."
                    : "🔍 No results for your search. Try different keywords or select a different category."}
                </div>
              ) : (
                sortedCats.map(cat => (
                  <div key={cat}>
                    <div className="dir-section-label">{cat}</div>
                    <div className="dir-grid">
                      {grouped[cat].map(e => {
                        const c = colors(e.category);
                        return (
                          <div
                            key={e.id}
                            className={`dir-card${selected?.id === e.id ? " selected" : ""}`}
                            onClick={() => setSelected(selected?.id === e.id ? null : e)}
                          >
                            <div className="dir-card-top">
                              {e.photo_url
                                ? <img src={e.photo_url} alt={e.name} style={{width:"38px",height:"38px",borderRadius:"50%",objectFit:"cover",flexShrink:0}} />
                                : <div className="dir-avatar" style={{background:c.avatar,color:c.text}}>{initials(e.name)}</div>
                              }
                              <div className="dir-card-info">
                                {e.category === "Lobbyist" && e.department && <div className="dir-card-title" style={{fontWeight:600,opacity:0.8}}>{e.department}</div>}
                                <div className="dir-card-name">{e.name}</div>
                                {e.title && <div className="dir-card-title">{e.title}</div>}
                              </div>
                            </div>
                            <div className="dir-card-meta">
                              {e.phone && <div className="dir-meta-row">📞 {e.phone}</div>}
                              {e.email && <div className="dir-meta-row">✉ {e.email}</div>}
                              {e.website && <div className="dir-meta-row">🌐 {e.website.replace(/^https?:\/\//, "")}</div>}
                            </div>
                            <div
                              className="dir-cat-badge"
                              style={{background:c.bg,border:`1px solid ${c.border}`,color:c.text}}
                            >
                              {e.category}
                            </div>
                            <button
                              className="dir-suggest-link"
                              onClick={ev => { ev.stopPropagation(); setSuggestEntry(e); }}
                            >
                              Suggest an edit
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* DETAIL PANEL */}
          {selected && (() => {
            const c = colors(selected.category);
            return (
              <div className="dir-detail">
                <div className="dd-head" style={{position:"relative"}}>
                  <button className="dd-close" onClick={() => setSelected(null)}>✕</button>
                  {selected.photo_url
                    ? <img src={selected.photo_url} alt={selected.name} style={{width:"64px",height:"64px",borderRadius:"50%",objectFit:"cover",marginBottom:"0.85rem",border:"2px solid var(--border)"}} />
                    : <div className="dd-avatar" style={{background:c.avatar,color:c.text}}>{initials(selected.name)}</div>
                  }
                  <div className="dd-name">{selected.name}</div>
                  {selected.title && <div className="dd-title">{selected.title}</div>}
                  {selected.department && <div className="dd-title">{selected.department}</div>}
                  <div
                    className="dir-cat-badge"
                    style={{background:c.bg,border:`1px solid ${c.border}`,color:c.text}}
                  >
                    {selected.category}
                    {selected.party && ` · ${selected.party === "D" ? "Democrat" : selected.party === "R" ? "Republican" : "Independent"}`}
                  </div>
                </div>
                <div className="dd-body">
                  <div>
                    <div className="dd-section-title">Contact</div>
                    {selected.phone && (
                      <div className="dd-field">
                        <div className="dd-label">Phone</div>
                        <a href={`tel:${selected.phone}`} className="dd-link">{selected.phone}</a>
                      </div>
                    )}
                    {selected.email && (
                      <div className="dd-field">
                        <div className="dd-label">Email</div>
                        <a href={`mailto:${selected.email}`} className="dd-link">{selected.email}</a>
                      </div>
                    )}
                    {selected.website && (
                      <div className="dd-field">
                        <div className="dd-label">Website</div>
                        <a href={selected.website} target="_blank" rel="noreferrer" className="dd-link">
                          {selected.website.replace(/^https?:\/\//, "")}
                        </a>
                      </div>
                    )}
                    {selected.address && (
                      <div className="dd-field">
                        <div className="dd-label">Address</div>
                        <div className="dd-value">{selected.address}</div>
                      </div>
                    )}
                    {!selected.phone && !selected.email && !selected.website && !selected.address && (
                      <div style={{fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--muted)"}}>No contact info on file.</div>
                    )}
                  </div>
                  {selected.notes && (
                    <div>
                      <div className="dd-section-title">Notes</div>
                      <div className="dd-notes">{selected.notes}</div>
                    </div>
                  )}
                  <div style={{borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'auto'}}>
                    <button
                      className="dir-suggest-link"
                      style={{fontSize:'0.65rem'}}
                      onClick={() => setSuggestEntry(selected)}
                    >
                      ✎ Suggest a correction or addition
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      {suggestEntry !== null && (
        <SuggestModal entry={suggestEntry} onClose={() => setSuggestEntry(null)} />
      )}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </>
  );
}
